import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Sidebar from '@/components/layout/Sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  Settings, 
  User, 
  Building2, 
  Clock, 
  MapPin, 
  Truck, 
  DollarSign, 
  Bell, 
  Shield,
  Save,
  Plus,
  Trash2,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timezones = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Phoenix", label: "Arizona (MST)" },
];

const equipmentTypes = [
  { value: "dry_van", label: "Dry Van" },
  { value: "reefer", label: "Refrigerated" },
  { value: "flatbed", label: "Flatbed" },
  { value: "step_deck", label: "Step Deck" },
  { value: "lowboy", label: "Lowboy" },
  { value: "tanker", label: "Tanker" },
  { value: "box_truck", label: "Box Truck" },
  { value: "sprinter", label: "Sprinter Van" },
];

const usStates = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const SettingsPage = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [newVehicle, setNewVehicle] = useState({
    vehicle_type: '', license_plate: '', year: '', make: '', model: '', capacity_weight: ''
  });
  const [newLocation, setNewLocation] = useState({
    name: '', address: '', city: '', state: '', zip_code: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const updateSettings = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const updateBusinessHours = (day, field, value) => {
    setSettings(prev => ({
      ...prev,
      business_hours: prev.business_hours?.map(h => 
        h.day === day ? { ...h, [field]: value } : h
      ) || []
    }));
  };

  const addVehicle = async () => {
    try {
      await axios.post(`${API}/settings/vehicle`, newVehicle);
      toast.success('Vehicle added');
      setVehicleDialogOpen(false);
      setNewVehicle({ vehicle_type: '', license_plate: '', year: '', make: '', model: '', capacity_weight: '' });
      fetchSettings();
    } catch (error) {
      toast.error('Failed to add vehicle');
    }
  };

  const removeVehicle = async (vehicleId) => {
    try {
      await axios.delete(`${API}/settings/vehicle/${vehicleId}`);
      toast.success('Vehicle removed');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to remove vehicle');
    }
  };

  const addLocation = async () => {
    try {
      await axios.post(`${API}/settings/location`, newLocation);
      toast.success('Location added');
      setLocationDialogOpen(false);
      setNewLocation({ name: '', address: '', city: '', state: '', zip_code: '' });
      fetchSettings();
    } catch (error) {
      toast.error('Failed to add location');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar role={user?.role} />
        <main className="lg:ml-64 p-6 lg:p-10">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-primary" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar role={user?.role} />
      <main className="lg:ml-64 p-6 lg:p-10" data-testid="settings-page">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-heading font-bold tracking-tight">Settings</h1>
              <p className="text-muted-foreground mt-1">Configure your account and preferences</p>
            </div>
            <Button 
              onClick={handleSave} 
              disabled={isSaving}
              className="bg-primary hover:bg-primary/90 shadow-glow"
              data-testid="save-settings-btn"
            >
              {isSaving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>

          <Tabs defaultValue="profile" className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-5 h-auto gap-2 bg-transparent p-0">
              <TabsTrigger value="profile" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-profile">
                <User className="w-4 h-4 mr-2" /> Profile
              </TabsTrigger>
              <TabsTrigger value="business" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-business">
                <Building2 className="w-4 h-4 mr-2" /> Business
              </TabsTrigger>
              <TabsTrigger value="operations" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-operations">
                <Clock className="w-4 h-4 mr-2" /> Operations
              </TabsTrigger>
              {(user?.role === 'dispatcher' || user?.role === 'driver') && (
                <TabsTrigger value="fleet" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-fleet">
                  <Truck className="w-4 h-4 mr-2" /> Fleet
                </TabsTrigger>
              )}
              <TabsTrigger value="notifications" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-notifications">
                <Bell className="w-4 h-4 mr-2" /> Notifications
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile" className="space-y-6">
              <Card className="card-hover">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <User className="w-5 h-5 text-primary" /> Personal Information
                  </CardTitle>
                  <CardDescription>Your basic profile details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="display_name">Display Name</Label>
                      <Input
                        id="display_name"
                        value={settings?.display_name || ''}
                        onChange={(e) => updateSettings('display_name', e.target.value)}
                        data-testid="input-display-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={settings?.email || ''}
                        onChange={(e) => updateSettings('email', e.target.value)}
                        data-testid="input-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        value={settings?.phone || ''}
                        onChange={(e) => updateSettings('phone', e.target.value)}
                        placeholder="(555) 123-4567"
                        data-testid="input-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="secondary_phone">Secondary Phone</Label>
                      <Input
                        id="secondary_phone"
                        value={settings?.secondary_phone || ''}
                        onChange={(e) => updateSettings('secondary_phone', e.target.value)}
                        placeholder="Optional"
                        data-testid="input-secondary-phone"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      value={settings?.company_name || ''}
                      onChange={(e) => updateSettings('company_name', e.target.value)}
                      data-testid="input-company-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="timezone">Timezone</Label>
                    <Select value={settings?.timezone || 'America/Chicago'} onValueChange={(v) => updateSettings('timezone', v)}>
                      <SelectTrigger data-testid="select-timezone">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {timezones.map(tz => (
                          <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Driver-specific CDL Info */}
              {user?.role === 'driver' && (
                <Card className="card-hover">
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Shield className="w-5 h-5 text-primary" /> CDL Information
                    </CardTitle>
                    <CardDescription>Your commercial driver's license details</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="cdl_number">CDL Number</Label>
                        <Input
                          id="cdl_number"
                          value={settings?.cdl_number || ''}
                          onChange={(e) => updateSettings('cdl_number', e.target.value)}
                          data-testid="input-cdl-number"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cdl_class">CDL Class</Label>
                        <Select value={settings?.cdl_class || ''} onValueChange={(v) => updateSettings('cdl_class', v)}>
                          <SelectTrigger data-testid="select-cdl-class">
                            <SelectValue placeholder="Select class" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="A">Class A</SelectItem>
                            <SelectItem value="B">Class B</SelectItem>
                            <SelectItem value="C">Class C</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cdl_expiry">Expiry Date</Label>
                        <Input
                          id="cdl_expiry"
                          type="date"
                          value={settings?.cdl_expiry_date || ''}
                          onChange={(e) => updateSettings('cdl_expiry_date', e.target.value)}
                          data-testid="input-cdl-expiry"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="home_terminal">Home Terminal</Label>
                      <Input
                        id="home_terminal"
                        value={settings?.home_terminal || ''}
                        onChange={(e) => updateSettings('home_terminal', e.target.value)}
                        placeholder="City, State"
                        data-testid="input-home-terminal"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="max_driving_hours">Max Driving Hours/Day</Label>
                      <Input
                        id="max_driving_hours"
                        type="number"
                        value={settings?.max_driving_hours || 11}
                        onChange={(e) => updateSettings('max_driving_hours', parseInt(e.target.value))}
                        data-testid="input-max-hours"
                      />
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Business Tab */}
            <TabsContent value="business" className="space-y-6">
              <Card className="card-hover">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary" /> Business Details
                  </CardTitle>
                  <CardDescription>Legal and regulatory information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="business_type">Business Type</Label>
                      <Select value={settings?.business_type || ''} onValueChange={(v) => updateSettings('business_type', v)}>
                        <SelectTrigger data-testid="select-business-type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="shipper">Shipper</SelectItem>
                          <SelectItem value="carrier">Carrier</SelectItem>
                          <SelectItem value="broker">Freight Broker</SelectItem>
                          <SelectItem value="3pl">3PL Provider</SelectItem>
                          <SelectItem value="owner_operator">Owner Operator</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tax_id">Tax ID / EIN</Label>
                      <Input
                        id="tax_id"
                        value={settings?.tax_id || ''}
                        onChange={(e) => updateSettings('tax_id', e.target.value)}
                        placeholder="XX-XXXXXXX"
                        data-testid="input-tax-id"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dot_number">DOT Number</Label>
                      <Input
                        id="dot_number"
                        value={settings?.dot_number || ''}
                        onChange={(e) => updateSettings('dot_number', e.target.value)}
                        data-testid="input-dot-number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mc_number">MC Number</Label>
                      <Input
                        id="mc_number"
                        value={settings?.mc_number || ''}
                        onChange={(e) => updateSettings('mc_number', e.target.value)}
                        data-testid="input-mc-number"
                      />
                    </div>
                  </div>

                  <Separator className="my-6" />

                  <h3 className="font-heading font-semibold text-lg">Insurance Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="insurance_provider">Insurance Provider</Label>
                      <Input
                        id="insurance_provider"
                        value={settings?.insurance_provider || ''}
                        onChange={(e) => updateSettings('insurance_provider', e.target.value)}
                        data-testid="input-insurance-provider"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="insurance_policy">Policy Number</Label>
                      <Input
                        id="insurance_policy"
                        value={settings?.insurance_policy_number || ''}
                        onChange={(e) => updateSettings('insurance_policy_number', e.target.value)}
                        data-testid="input-insurance-policy"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="insurance_expiry">Policy Expiry</Label>
                      <Input
                        id="insurance_expiry"
                        type="date"
                        value={settings?.insurance_expiry_date || ''}
                        onChange={(e) => updateSettings('insurance_expiry_date', e.target.value)}
                        data-testid="input-insurance-expiry"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cargo_limit">Cargo Insurance Limit ($)</Label>
                      <Input
                        id="cargo_limit"
                        type="number"
                        value={settings?.cargo_insurance_limit || ''}
                        onChange={(e) => updateSettings('cargo_insurance_limit', parseFloat(e.target.value))}
                        placeholder="100000"
                        data-testid="input-cargo-limit"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Pay Rates Card */}
              <Card className="card-hover">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-primary" /> Payment Settings
                  </CardTitle>
                  <CardDescription>Configure pay rates and payment preferences</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="payment_terms">Payment Terms (Days)</Label>
                      <Input
                        id="payment_terms"
                        type="number"
                        value={settings?.payment_terms_days || 30}
                        onChange={(e) => updateSettings('payment_terms_days', parseInt(e.target.value))}
                        data-testid="input-payment-terms"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="payment_method">Preferred Payment Method</Label>
                      <Select value={settings?.preferred_payment_method || 'ach'} onValueChange={(v) => updateSettings('preferred_payment_method', v)}>
                        <SelectTrigger data-testid="select-payment-method">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ach">ACH Transfer</SelectItem>
                          <SelectItem value="check">Check</SelectItem>
                          <SelectItem value="quick_pay">Quick Pay</SelectItem>
                          <SelectItem value="wire">Wire Transfer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="quick_pay_fee">Quick Pay Fee (%)</Label>
                      <Input
                        id="quick_pay_fee"
                        type="number"
                        step="0.5"
                        value={settings?.quick_pay_fee_percentage || 2.5}
                        onChange={(e) => updateSettings('quick_pay_fee_percentage', parseFloat(e.target.value))}
                        data-testid="input-quick-pay-fee"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="min_rate">Minimum Rate per Mile ($)</Label>
                      <Input
                        id="min_rate"
                        type="number"
                        step="0.01"
                        value={settings?.min_rate_per_mile || ''}
                        onChange={(e) => updateSettings('min_rate_per_mile', parseFloat(e.target.value))}
                        placeholder="2.50"
                        data-testid="input-min-rate"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Operations Tab */}
            <TabsContent value="operations" className="space-y-6">
              <Card className="card-hover">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" /> Business Hours
                  </CardTitle>
                  <CardDescription>Set your operating hours for each day</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {settings?.business_hours?.map((hours) => (
                      <div key={hours.day} className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                        <div className="w-28 capitalize font-medium">{hours.day}</div>
                        <Switch
                          checked={!hours.is_closed}
                          onCheckedChange={(checked) => updateBusinessHours(hours.day, 'is_closed', !checked)}
                          data-testid={`switch-${hours.day}`}
                        />
                        <span className="text-sm text-muted-foreground w-12">{hours.is_closed ? 'Closed' : 'Open'}</span>
                        {!hours.is_closed && (
                          <>
                            <Input
                              type="time"
                              value={hours.open_time}
                              onChange={(e) => updateBusinessHours(hours.day, 'open_time', e.target.value)}
                              className="w-32"
                              data-testid={`input-${hours.day}-open`}
                            />
                            <span className="text-muted-foreground">to</span>
                            <Input
                              type="time"
                              value={hours.close_time}
                              onChange={(e) => updateBusinessHours(hours.day, 'close_time', e.target.value)}
                              className="w-32"
                              data-testid={`input-${hours.day}-close`}
                            />
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="card-hover">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" /> Pickup & Delivery Settings
                  </CardTitle>
                  <CardDescription>Default time windows and booking preferences</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="pickup_window">Default Pickup Window (Hours)</Label>
                      <Input
                        id="pickup_window"
                        type="number"
                        value={settings?.default_pickup_window_hours || 2}
                        onChange={(e) => updateSettings('default_pickup_window_hours', parseInt(e.target.value))}
                        data-testid="input-pickup-window"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="delivery_window">Default Delivery Window (Hours)</Label>
                      <Input
                        id="delivery_window"
                        type="number"
                        value={settings?.default_delivery_window_hours || 4}
                        onChange={(e) => updateSettings('default_delivery_window_hours', parseInt(e.target.value))}
                        data-testid="input-delivery-window"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="advance_booking">Advance Booking (Days)</Label>
                      <Input
                        id="advance_booking"
                        type="number"
                        value={settings?.advance_booking_days || 7}
                        onChange={(e) => updateSettings('advance_booking_days', parseInt(e.target.value))}
                        data-testid="input-advance-booking"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="same_day_cutoff">Same-Day Cutoff Time</Label>
                      <Input
                        id="same_day_cutoff"
                        type="time"
                        value={settings?.same_day_cutoff_time || '12:00'}
                        onChange={(e) => updateSettings('same_day_cutoff_time', e.target.value)}
                        data-testid="input-same-day-cutoff"
                      />
                    </div>
                  </div>

                  <Separator className="my-6" />

                  <div className="space-y-2">
                    <Label>Service Regions</Label>
                    <div className="flex flex-wrap gap-2">
                      {usStates.map(state => (
                        <Button
                          key={state}
                          variant={settings?.service_regions?.includes(state) ? "default" : "outline"}
                          size="sm"
                          className={settings?.service_regions?.includes(state) ? "bg-primary" : ""}
                          onClick={() => {
                            const current = settings?.service_regions || [];
                            const updated = current.includes(state)
                              ? current.filter(s => s !== state)
                              : [...current, state];
                            updateSettings('service_regions', updated);
                          }}
                          data-testid={`region-${state}`}
                        >
                          {state}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Locations */}
              <Card className="card-hover">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-primary" /> Locations
                    </CardTitle>
                    <CardDescription>Your pickup and delivery locations</CardDescription>
                  </div>
                  <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" data-testid="add-location-btn">
                        <Plus className="w-4 h-4 mr-1" /> Add Location
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Location</DialogTitle>
                        <DialogDescription>Add a new pickup or delivery location</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Location Name</Label>
                          <Input value={newLocation.name} onChange={(e) => setNewLocation({...newLocation, name: e.target.value})} placeholder="Warehouse A" />
                        </div>
                        <div className="space-y-2">
                          <Label>Address</Label>
                          <Input value={newLocation.address} onChange={(e) => setNewLocation({...newLocation, address: e.target.value})} placeholder="123 Industrial Blvd" />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="space-y-2">
                            <Label>City</Label>
                            <Input value={newLocation.city} onChange={(e) => setNewLocation({...newLocation, city: e.target.value})} />
                          </div>
                          <div className="space-y-2">
                            <Label>State</Label>
                            <Select value={newLocation.state} onValueChange={(v) => setNewLocation({...newLocation, state: v})}>
                              <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
                              <SelectContent>
                                {usStates.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>ZIP</Label>
                            <Input value={newLocation.zip_code} onChange={(e) => setNewLocation({...newLocation, zip_code: e.target.value})} />
                          </div>
                        </div>
                        <Button className="w-full" onClick={addLocation}>Add Location</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  {settings?.additional_locations?.length > 0 ? (
                    <div className="space-y-2">
                      {settings.additional_locations.map((loc, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div>
                            <p className="font-medium">{loc.name}</p>
                            <p className="text-sm text-muted-foreground">{loc.address}, {loc.city}, {loc.state} {loc.zip_code}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No locations added yet</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Fleet Tab */}
            {(user?.role === 'dispatcher' || user?.role === 'driver') && (
              <TabsContent value="fleet" className="space-y-6">
                <Card className="card-hover">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="font-heading flex items-center gap-2">
                        <Truck className="w-5 h-5 text-primary" /> Fleet Management
                      </CardTitle>
                      <CardDescription>Manage your vehicles and equipment</CardDescription>
                    </div>
                    <Dialog open={vehicleDialogOpen} onOpenChange={setVehicleDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline" data-testid="add-vehicle-btn">
                          <Plus className="w-4 h-4 mr-1" /> Add Vehicle
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add Vehicle</DialogTitle>
                          <DialogDescription>Add a new vehicle to your fleet</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Vehicle Type</Label>
                              <Select value={newVehicle.vehicle_type} onValueChange={(v) => setNewVehicle({...newVehicle, vehicle_type: v})}>
                                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                                <SelectContent>
                                  {equipmentTypes.map(eq => <SelectItem key={eq.value} value={eq.value}>{eq.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>License Plate</Label>
                              <Input value={newVehicle.license_plate} onChange={(e) => setNewVehicle({...newVehicle, license_plate: e.target.value})} />
                            </div>
                            <div className="space-y-2">
                              <Label>Year</Label>
                              <Input type="number" value={newVehicle.year} onChange={(e) => setNewVehicle({...newVehicle, year: e.target.value})} />
                            </div>
                            <div className="space-y-2">
                              <Label>Make</Label>
                              <Input value={newVehicle.make} onChange={(e) => setNewVehicle({...newVehicle, make: e.target.value})} placeholder="Freightliner" />
                            </div>
                            <div className="space-y-2">
                              <Label>Model</Label>
                              <Input value={newVehicle.model} onChange={(e) => setNewVehicle({...newVehicle, model: e.target.value})} placeholder="Cascadia" />
                            </div>
                            <div className="space-y-2">
                              <Label>Capacity (lbs)</Label>
                              <Input type="number" value={newVehicle.capacity_weight} onChange={(e) => setNewVehicle({...newVehicle, capacity_weight: e.target.value})} placeholder="45000" />
                            </div>
                          </div>
                          <Button className="w-full" onClick={addVehicle}>Add Vehicle</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 mb-6">
                      <Label htmlFor="fleet_size">Total Fleet Size</Label>
                      <Input
                        id="fleet_size"
                        type="number"
                        value={settings?.fleet_size || ''}
                        onChange={(e) => updateSettings('fleet_size', parseInt(e.target.value))}
                        placeholder="Number of trucks"
                        className="max-w-xs"
                        data-testid="input-fleet-size"
                      />
                    </div>

                    {settings?.vehicles?.length > 0 ? (
                      <div className="space-y-2">
                        {settings.vehicles.map((vehicle) => (
                          <div key={vehicle.vehicle_id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                <Truck className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium">{vehicle.year} {vehicle.make} {vehicle.model}</p>
                                <p className="text-sm text-muted-foreground">
                                  {vehicle.license_plate} • {equipmentTypes.find(e => e.value === vehicle.vehicle_type)?.label || vehicle.vehicle_type}
                                </p>
                              </div>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => removeVehicle(vehicle.vehicle_id)} data-testid={`remove-vehicle-${vehicle.vehicle_id}`}>
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">No vehicles added yet</p>
                    )}

                    <Separator className="my-6" />

                    <div className="space-y-2">
                      <Label>Preferred Equipment Types</Label>
                      <div className="flex flex-wrap gap-2">
                        {equipmentTypes.map(eq => (
                          <Button
                            key={eq.value}
                            variant={settings?.preferred_equipment_types?.includes(eq.value) ? "default" : "outline"}
                            size="sm"
                            className={settings?.preferred_equipment_types?.includes(eq.value) ? "bg-primary" : ""}
                            onClick={() => {
                              const current = settings?.preferred_equipment_types || [];
                              const updated = current.includes(eq.value)
                                ? current.filter(t => t !== eq.value)
                                : [...current, eq.value];
                              updateSettings('preferred_equipment_types', updated);
                            }}
                            data-testid={`equipment-${eq.value}`}
                          >
                            {eq.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* Notifications Tab */}
            <TabsContent value="notifications" className="space-y-6">
              <Card className="card-hover">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Bell className="w-5 h-5 text-primary" /> Notification Preferences
                  </CardTitle>
                  <CardDescription>Choose how you want to be notified</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">Email Notifications</p>
                        <p className="text-sm text-muted-foreground">Receive updates via email</p>
                      </div>
                      <Switch
                        checked={settings?.email_notifications ?? true}
                        onCheckedChange={(checked) => updateSettings('email_notifications', checked)}
                        data-testid="switch-email-notifications"
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">SMS Notifications</p>
                        <p className="text-sm text-muted-foreground">Receive text message alerts</p>
                      </div>
                      <Switch
                        checked={settings?.sms_notifications ?? false}
                        onCheckedChange={(checked) => updateSettings('sms_notifications', checked)}
                        data-testid="switch-sms-notifications"
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">Push Notifications</p>
                        <p className="text-sm text-muted-foreground">Browser push notifications</p>
                      </div>
                      <Switch
                        checked={settings?.push_notifications ?? true}
                        onCheckedChange={(checked) => updateSettings('push_notifications', checked)}
                        data-testid="switch-push-notifications"
                      />
                    </div>
                  </div>

                  <Separator />

                  <h3 className="font-heading font-semibold">Alert Types</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">New Loads</p>
                        <p className="text-sm text-muted-foreground">When new loads are available</p>
                      </div>
                      <Switch
                        checked={settings?.notify_on_new_loads ?? true}
                        onCheckedChange={(checked) => updateSettings('notify_on_new_loads', checked)}
                        data-testid="switch-new-loads"
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">Payment Updates</p>
                        <p className="text-sm text-muted-foreground">Payment status changes</p>
                      </div>
                      <Switch
                        checked={settings?.notify_on_payment_updates ?? true}
                        onCheckedChange={(checked) => updateSettings('notify_on_payment_updates', checked)}
                        data-testid="switch-payment-updates"
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">Document Updates</p>
                        <p className="text-sm text-muted-foreground">BOL signatures and documents</p>
                      </div>
                      <Switch
                        checked={settings?.notify_on_document_updates ?? true}
                        onCheckedChange={(checked) => updateSettings('notify_on_document_updates', checked)}
                        data-testid="switch-document-updates"
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">Daily Summary</p>
                        <p className="text-sm text-muted-foreground">Receive daily activity summary</p>
                      </div>
                      <Switch
                        checked={settings?.daily_summary_email ?? false}
                        onCheckedChange={(checked) => updateSettings('daily_summary_email', checked)}
                        data-testid="switch-daily-summary"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;
